<script setup lang="ts">
import SelectWithIcon from '@/components/BaseComponents/SelectWithIcon.vue';
import { type MessageSchema } from '@/i18n';
import { computed, ref, watch, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

// Define props and emits
const props = defineProps<{
  modelValue: boolean;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

interface WorkingPeriodOption {
  name: string;
  value: boolean;
  icon: string;
}

const selectedOption: Ref<WorkingPeriodOption | null> = ref(null);

const options: WorkingPeriodOption[] = [
  {
    name: computed(() => t('profile.profile.restaurantTab.settings.workingPeriods.allowOption')).value,
    value: true,
    icon: 'pi pi-check-circle',
  },
  {
    name: computed(() => t('profile.profile.restaurantTab.settings.workingPeriods.restrictOption')).value,
    value: false,
    icon: 'pi pi-times-circle',
  },
];

// Initialize selected option based on modelValue
const getDefaultOption = (): WorkingPeriodOption | null => {
  return options.find((option) => option.value === props.modelValue) || null;
};

const initializeSelection = () => {
  selectedOption.value = getDefaultOption();
};

// Initialize on mount
initializeSelection();

// Watch for external changes to modelValue
watch(
  () => props.modelValue,
  () => {
    initializeSelection();
  },
);

const toggleSelection = (option: WorkingPeriodOption) => {
  selectedOption.value = option;
  emit('update:modelValue', option.value);
};
</script>

<template>
  <div class="grid grid-cols-2 gap-4">
    <div v-for="option in options" :key="option.value.toString()">
      <SelectWithIcon
        :selected="selectedOption?.value === option.value"
        :disabled="disabled"
        :class="{
          'cursor-not-allowed opacity-50': disabled,
          'border-green-300 bg-green-100': selectedOption?.value === option.value && option.value === true,
          'border-red-300 bg-red-100': selectedOption?.value === option.value && option.value === false,
        }"
        @handle-select="() => !disabled && toggleSelection(option)"
      >
        <template #icon>
          <i
            :class="[
              option.icon,
              selectedOption?.value === option.value && option.value === true
                ? 'text-green-600'
                : selectedOption?.value === option.value && option.value === false
                  ? 'text-red-600'
                  : 'text-gray-500',
            ]"
          ></i>
        </template>
        <template #label>{{ option.name }}</template>
      </SelectWithIcon>
    </div>
  </div>
</template>
